import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import LoadingScreen from "@/components/LoadingScreen";

/**
 * Route-level gate restricting access to the director-only super-admin pages
 * (admin/users, admin/security, admin/feature-flags).
 *
 * Verifies via the `is_director` security-definer DB function rather than
 * client state, so URL access cannot be bypassed by anyone with the
 * `admin` role.
 */
export default function DirectorGate({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [state, setState] = useState<"loading" | "allowed" | "denied">("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const uid = sessionData.session?.user?.id;
      if (!uid) {
        if (!cancelled) setState("denied");
        return;
      }
      const { data, error } = await (supabase.rpc as any)("is_director", { _user: uid });
      if (cancelled) return;
      if (error || !data) setState("denied");
      else setState("allowed");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "loading") return <LoadingScreen />;
  if (state === "denied") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md text-center space-y-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mx-auto">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-foreground">Director access only</h1>
          <p className="text-muted-foreground">
            This area is restricted to the company director account. If you believe this is an error, contact the platform owner.
          </p>
          <button
            onClick={() => navigate("/portal", { replace: true })}
            className="inline-flex items-center justify-center px-6 py-2.5 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
          >
            Return to dashboard
          </button>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
